/**
 * The front door's QR code (mystic-page 2026-10-29, Studio "Your front
 * door"; API.md §2 `qr_text`). Drawn here from the text, never a server
 * image, with qrcode-generator (MIT, pinned 1.4.4, no dependencies).
 *
 * Error correction H (30%), so the Mystic's portrait can sit over the middle
 * fifth and the code still scans.
 */
import qrcode from 'qrcode-generator'

export const QR_MARGIN = 4          // the quiet zone, in modules (the standard's 4)
export const QR_FG = '#2a1648'      // the app's deep plum on cream: high contrast
export const QR_BG = '#fbf6ea'

/** The code's modules: { size, dark(row, col) }. */
export function qrMatrix (text) {
  const q = qrcode(0, 'H')
  q.addData(String(text || ''))
  q.make()
  const size = q.getModuleCount()
  return { size, dark: (r, c) => q.isDark(r, c) }
}

/** The square the portrait takes, in modules: odd-sized, centred, about a fifth. */
export function logoBox (size) {
  let n = Math.round(size * 0.2)
  if (n % 2 !== size % 2) n += 1
  return { start: (size - n) / 2, size: n }
}

function inBox (r, c, b) {
  return b && r >= b.start && r < b.start + b.size && c >= b.start && c < b.start + b.size
}

/** A standalone SVG (for download and display); the portrait, if any, in the middle. */
export function qrSvg (text, { logo = '', fg = QR_FG, bg = QR_BG } = {}) {
  const m = qrMatrix(text)
  const box = logo ? logoBox(m.size) : null
  const full = m.size + 2 * QR_MARGIN
  let d = ''
  for (let r = 0; r < m.size; r++) {
    for (let c = 0; c < m.size; c++) {
      if (m.dark(r, c) && !inBox(r, c, box)) d += `M${c + QR_MARGIN} ${r + QR_MARGIN}h1v1h-1z`
    }
  }
  let img = ''
  if (box) {
    const pad = 0.6
    const x = box.start + QR_MARGIN + pad
    const s = box.size - 2 * pad
    img = `<rect x="${box.start + QR_MARGIN}" y="${box.start + QR_MARGIN}" width="${box.size}" height="${box.size}" rx="1.2" fill="${bg}"/>`
      + `<image href="${escapeAttr(logo)}" x="${x}" y="${x}" width="${s}" height="${s}" preserveAspectRatio="xMidYMid slice" clip-path="url(#qr-logo)"/>`
      + `<defs><clipPath id="qr-logo"><rect x="${x}" y="${x}" width="${s}" height="${s}" rx="1"/></clipPath></defs>`
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${full} ${full}" shape-rendering="crispEdges">`
    + `<rect width="${full}" height="${full}" fill="${bg}"/>`
    + `<path d="${d}" fill="${fg}"/>${img}</svg>`
}

function escapeAttr (s) {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

/**
 * A PNG of the code on a canvas (scale px per module), the portrait drawn in
 * when the browser lets it (a cross-origin image without CORS would taint the
 * canvas: then the code goes out without it, still scannable). → data URL.
 */
export async function qrPng (text, { logo = '', scale = 16, fg = QR_FG, bg = QR_BG, doc = document } = {}) {
  const m = qrMatrix(text)
  const full = (m.size + 2 * QR_MARGIN) * scale
  const canvas = doc.createElement('canvas')
  canvas.width = full; canvas.height = full
  const ctx = canvas.getContext('2d')
  const draw = (withLogo) => {
    const box = withLogo ? logoBox(m.size) : null
    ctx.fillStyle = bg; ctx.fillRect(0, 0, full, full)
    ctx.fillStyle = fg
    for (let r = 0; r < m.size; r++) for (let c = 0; c < m.size; c++) {
      if (m.dark(r, c) && !inBox(r, c, box)) ctx.fillRect((c + QR_MARGIN) * scale, (r + QR_MARGIN) * scale, scale, scale)
    }
    return box
  }
  let img = null
  if (logo) img = await loadImage(logo, doc).catch(() => null)
  const box = draw(!!img)
  if (img && box) {
    const pad = 0.6 * scale
    const x = (box.start + QR_MARGIN) * scale + pad
    const s = box.size * scale - 2 * pad
    try {
      ctx.drawImage(img, x, x, s, s)
      return canvas.toDataURL('image/png')
    } catch (e) {
      // Tainted for good: a fresh canvas, the code alone.
      return qrPng(text, { scale, fg, bg, doc })
    }
  }
  return canvas.toDataURL('image/png')
}

function loadImage (src, doc) {
  return new Promise((resolve, reject) => {
    const img = new (doc.defaultView || window).Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}
